import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Scissors, Mail, Lock, AlertTriangle } from "lucide-react";

function formatError(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return detail.msg || JSON.stringify(detail);
}

function googleLogin() {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const redirectUrl = window.location.origin + "/dashboard";
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
}

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/dashboard";
  const prefillEmail = location.state?.prefillEmail || "";

  const [email, setEmail] = useState(prefillEmail);
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      navigate(from, { replace: true });
    } catch (err) {
      if (!err.response) {
        setError("Network issue. Check your connection and try again.");
      } else {
        setError(formatError(err.response?.data?.detail) || err.message);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-6 py-16 animate-fade-in">
      <div className="w-full max-w-md cm-card p-8 md:p-10">
        <div className="flex items-center gap-2 mb-8">
          <Scissors className="w-5 h-5 text-copper" />
          <span className="cm-heading text-xl">SIGN IN</span>
        </div>

        <button
          onClick={googleLogin}
          data-testid="google-login-button"
          className="cm-btn-secondary w-full flex items-center justify-center gap-3 mb-5"
        >
          <span className="font-bold text-base">G</span>
          <span>Continue with Google</span>
        </button>

        <div className="flex items-center gap-3 my-6">
          <div className="flex-1 h-px bg-ink-700" />
          <span className="text-[10px] tracking-widest uppercase text-gray-500">or</span>
          <div className="flex-1 h-px bg-ink-700" />
        </div>

        <form onSubmit={submit} className="space-y-4" noValidate aria-describedby={error ? "login-error" : undefined}>
          <div>
            <label htmlFor="login-email" className="cm-label block mb-2">Email</label>
            <div className="relative">
              <Mail className="absolute left-3 top-3.5 w-4 h-4 text-gray-500" aria-hidden="true" />
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="cm-input pl-10"
                placeholder="you@domain.com"
                data-testid="login-email-input"
              />
            </div>
          </div>
          <div>
            <label htmlFor="login-password" className="cm-label block mb-2">Password</label>
            <div className="relative">
              <Lock className="absolute left-3 top-3.5 w-4 h-4 text-gray-500" aria-hidden="true" />
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="cm-input pl-10"
                placeholder="••••••••"
                data-testid="login-password-input"
              />
            </div>
          </div>
          {error && (
            <div
              id="login-error"
              role="alert"
              aria-live="assertive"
              className="flex items-start gap-2 text-sm text-red-400"
              data-testid="login-error"
            >
              <AlertTriangle className="w-4 h-4 mt-0.5" aria-hidden="true" /> {error}
            </div>
          )}
          <button type="submit" disabled={busy} className="cm-btn-primary w-full" data-testid="login-submit-button">
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>

        <div className="mt-5 text-center">
          <Link
            to="/forgot-password"
            className="text-xs uppercase tracking-widest text-gray-400 hover:text-copper"
            data-testid="login-forgot-password-link"
          >
            Forgot password?
          </Link>
        </div>

        <p className="mt-6 text-sm text-gray-400 text-center">
          New here?{" "}
          <Link to="/register" className="text-copper hover:underline" data-testid="login-to-register-link">
            Create an account
          </Link>
        </p>
      </div>
    </div>
  );
}
